import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";

const VALIDATION_URL =
  "https://api.super-user-manager.com/sum-connector/api/auth/{entity_identification}/{user_identification}/login-page/validation";

const describedBy = (label: string): string => {
  const [, ...rest] = label.split(": ");
  return rest.length ? rest.join(": ") : label;
};

const IntegrationGuide = ({ callbackUrl }: { callbackUrl: string | null }) => {
  const [t] = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const prefix = "dashboard.features.loginTemplate.tempPart.integrationGuide";
  const exampleCallback = callbackUrl || "https://yourapp.com/login";

  return (
    <div className="w-full border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <span className="text-gray-700 text-sm font-semibold font-figtree">
          {t(`${prefix}.title`)}
        </span>
        {isOpen ? (
          <HiChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <HiChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="px-4 py-5 space-y-6 text-sm text-gray-600 leading-relaxed">
          <div>
            <h4 className="text-gray-800 font-semibold mb-1">
              {t(`${prefix}.step1Title`)}
            </h4>
            <p>{t(`${prefix}.step1Text`)}</p>
          </div>

          <div>
            <h4 className="text-gray-800 font-semibold mb-1">
              {t(`${prefix}.step2Title`)}
            </h4>
            <p>{t(`${prefix}.step2Text`)}</p>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 text-xs font-semibold mb-1">
              {t(`${prefix}.step2NoteTitle`)}
            </p>
            <p className="text-amber-700 text-xs leading-relaxed">
              {t(`${prefix}.step2Note`)}
            </p>
          </div>

          <div className="border-t border-dashed border-gray-300 pt-4">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-xs bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded">
                {t(`${prefix}.devSectionBadge`)}
              </span>
              <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded">
                {t(`${prefix}.devSectionPlanBadge`)}
              </span>
            </div>
            <p className="text-gray-500 text-xs">
              {t(`${prefix}.devSectionNote`)}
            </p>
          </div>

          <div>
            <h4 className="text-gray-800 font-semibold mb-1">
              {t(`${prefix}.step3Title`)}
            </h4>
            <p className="mb-3">{t(`${prefix}.step3Text`)}</p>

            <ul className="space-y-2 pl-1">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                <span>
                  <code className="text-xs bg-gray-100 text-red-600 px-1 py-0.5 rounded">
                    login_token
                  </code>
                  {": "}
                  {describedBy(t(`${prefix}.paramToken`) as string)}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                <span>
                  <code className="text-xs bg-gray-100 text-red-600 px-1 py-0.5 rounded">
                    last_connection
                  </code>
                  {": "}
                  {describedBy(t(`${prefix}.paramLastConnection`) as string)}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                <span>
                  <code className="text-xs bg-gray-100 text-red-600 px-1 py-0.5 rounded">
                    user_is_active
                  </code>
                  {": "}
                  {describedBy(t(`${prefix}.paramUserIsActive`) as string)}
                </span>
              </li>
            </ul>

            <p className="mt-3 text-gray-500 text-xs">
              {t(`${prefix}.step3Example`)}
            </p>
            <div className="mt-1 p-2.5 bg-gray-900 rounded-md overflow-x-auto">
              <code className="text-xs text-green-300 break-all">
                {exampleCallback}
                ?login_token=leMGsANR...&amp;last_connection=2026-03-26T22:18:04.812Z&amp;user_is_active=true
              </code>
            </div>
          </div>

          <div>
            <h4 className="text-gray-800 font-semibold mb-1">
              {t(`${prefix}.step4Title`)}
            </h4>
            <p className="mb-3">{t(`${prefix}.step4Text`)}</p>

            <p className="text-gray-500 text-xs mb-1">
              {t(`${prefix}.step4Endpoint`)}
            </p>
            <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto mb-3">
              <code className="text-xs text-amber-300 break-all">
                POST {VALIDATION_URL}
              </code>
            </div>

            <p className="text-gray-500 text-xs mb-1">
              {t(`${prefix}.step4AuthLabel`)}
            </p>
            <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto mb-1">
              <code className="text-xs text-amber-300 break-all">
                Authorization: Basic base64(user_identification:access_token)
              </code>
            </div>
            <p className="text-gray-500 text-xs mb-3">
              {t(`${prefix}.step4AuthHint`)}
            </p>

            <p className="text-gray-500 text-xs mb-1">
              {t(`${prefix}.step4Body`)}
            </p>
            <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto mb-3">
              <pre className="text-xs text-green-300">
                {`{
  "login_token": "leMGsANRHNsG1pja..."
}`}
              </pre>
            </div>

            <p>{t(`${prefix}.step4Success`)}</p>
          </div>

          <div>
            <h4 className="text-gray-800 font-semibold mb-1">
              {t(`${prefix}.step5Title`)}
            </h4>
            <p>{t(`${prefix}.step5Text`)}</p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-gray-800 font-semibold mb-2">
              {t(`${prefix}.securityTitle`)}
            </h4>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                <span>{t(`${prefix}.securityTip1`)}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                <span>{t(`${prefix}.securityTip2`)}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                <span>{t(`${prefix}.securityTip3`)}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                <span>{t(`${prefix}.securityTip4`)}</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationGuide;
